import { SelectableOptionMenuItemType } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import AdminTenant from 'models/adminTenant';
import { isSameDirectory } from 'models/identityProvider';
import Tenant from 'models/tenant';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetTenantsForPartner } from 'services/Api/partnerService';
import { apiGetTenantsForUser } from 'services/Api/tenantService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnString } from 'utils/sorting';

export enum TenantPickerMode {
  User = 1,
  Partner = 2,
}

export class PickerTenant {
  tenantId: string;

  alternativeTenantIds: string[];

  name: string;

  constructor() {
    this.tenantId = '';
    this.alternativeTenantIds = [];
    this.name = '';
  }
}

interface ITenantPickerProps {
  mode: TenantPickerMode;
  label?: string;
  selectedTenants?: string[];
  summary?: string;
  onSelect: (items: PickerTenant[]) => void;
  setAllTenants?: (tenants: PickerTenant[]) => void;
  emptySelectionMeansAll?: boolean;
}

const TenantPicker = (props: ITenantPickerProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [tenants, setTenants] = useState<PickerTenant[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadRequestId = useRef<number>(0);

  //
  // Effects
  //
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode]);

  useEffect(() => {
    setSelectedTenants(props.selectedTenants ?? []);
  }, [props.selectedTenants]);

  //
  // Loading
  //
  const isOrgUnit = (tenant: Tenant): boolean => {
    return isSameDirectory(tenant, appContext.user.tenant);
  };

  const loadData = async () => {
    const requestId = loadRequestId.current + 1;
    loadRequestId.current = requestId;

    try {
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      let tenants: PickerTenant[] = [];
      if (props.mode === TenantPickerMode.Partner) {
        //Show all customers for the partner
        const partnerTenants = await apiGetTenantsForPartner(accessToken, appContext.globalDataCache);
        tenants = partnerTenants.map((tenant: AdminTenant) => {
          const pickerTenant = new PickerTenant();
          pickerTenant.tenantId = tenant.tenantId;
          pickerTenant.alternativeTenantIds = [tenant.providerTenantId, tenant.parentId, tenant.topLevelParentId].filter(
            (tenantId): tenantId is string => tenantId !== undefined && tenantId !== '',
          );
          pickerTenant.name = tenant.displayName || tenant.name;

          return pickerTenant;
        });
      } else {
        //Show organizational units for the user
        const userTenants = await apiGetTenantsForUser(accessToken);
        tenants = userTenants
          .filter((t) => isOrgUnit(t))
          .map((tenant: Tenant) => {
            const pickerTenant = new PickerTenant();
            pickerTenant.tenantId = tenant.id;
            pickerTenant.name = tenant.name;

            return pickerTenant;
          });
      }
      tenants.sort((a, b) => sortOnString(a.name, b.name));

      if (requestId !== loadRequestId.current) return;

      setTenants(tenants);
      if (props.setAllTenants) {
        props.setAllTenants(tenants);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      if (requestId === loadRequestId.current) {
        setIsLoading(false);
      }
    }
  };

  //
  // Helpers
  //
  const isSameTenantId = (first: string | undefined, second: string | undefined): boolean => {
    return (first ?? '').toLowerCase() === (second ?? '').toLowerCase();
  };

  const getCanonicalTenantId = (tenantId: string): string => {
    const tenant = tenants.find(
      (tenant) =>
        isSameTenantId(tenant.tenantId, tenantId) ||
        tenant.alternativeTenantIds.some((alternativeTenantId) => isSameTenantId(alternativeTenantId, tenantId)),
    );

    return tenant?.tenantId ?? tenantId;
  };

  const getSelectedTenantIds = (): string[] => {
    return selectedTenants.map(getCanonicalTenantId);
  };

  const getDisplayedSelectedTenantIds = (): string[] => {
    const selectedTenantIds = getSelectedTenantIds();
    if (props.emptySelectionMeansAll && selectedTenantIds.length === 0) {
      return tenants.map((tenant) => tenant.tenantId);
    }

    return selectedTenantIds;
  };

  const updateSelectedTenants = (selectedTenantIds: string[]) => {
    const configTenantIds =
      props.emptySelectionMeansAll && selectedTenantIds.length === tenants.length ? [] : selectedTenantIds;
    setSelectedTenants(configTenantIds);
    props.onSelect(tenants.filter((tenant) => configTenantIds.includes(tenant.tenantId)));
  };

  const getOptions = () => {
    return [
      {
        key: '-1',
        text: t('widgets:Tenant.Select.SelectAll'),
        itemType: SelectableOptionMenuItemType.SelectAll,
      },
      {
        key: '0',
        text: '-',
        itemType: SelectableOptionMenuItemType.Divider,
      },
      ...tenants.map((tenant) => ({
        key: tenant.tenantId,
        text: tenant.name,
        data: tenant,
      })),
    ];
  };

  //
  // Main render
  //
  const label =
    props.label ??
    (props.mode === TenantPickerMode.User
      ? t('widgets:Tenant.Select.LabelOU')
      : t('widgets:Tenant.Select.LabelPartner'));

  if (isLoading) {
    return (
      <IPComboBox
        key="tenant-picker-loading"
        label={label}
        options={[]}
        disabled
        text={t('translation:General.Notifications.Loading')}
      />
    );
  }

  const selectedTenantIds = getDisplayedSelectedTenantIds();
  const allSelected = tenants.length > 0 && selectedTenantIds.length === tenants.length;

  return (
    <IPComboBox
      key="tenant-picker-loaded"
      label={label}
      options={getOptions()}
      multiSelect
      selectedKey={[allSelected ? '-1' : '0', ...selectedTenantIds]}
      useComboBoxAsMenuWidth
      onChange={(ev, option) => {
        if (option) {
          let selected: string[] = [];
          if (option?.itemType === SelectableOptionMenuItemType.SelectAll) {
            selected = props.emptySelectionMeansAll || !allSelected ? tenants.map((a) => a.tenantId) : [];
          } else {
            selected = option.selected
              ? [...new Set([...selectedTenantIds, option.key as string])]
              : selectedTenantIds.filter((t) => t !== option.key);
          }
          updateSelectedTenants(selected);
        }
      }}
      styles={{ optionsContainer: { maxHeight: '40vh' } }}
      text={props.summary ?? t('translation:General.Notifications.ItemCount', { count: selectedTenantIds.length })}
    />
  );
};

export default TenantPicker;
